// Mock layer for the Club-Network UI. Active only when REACT_APP_CN_MOCK === "1".
// Shapes match the FINAL cn-env contract (Backend #23):
//   • N2 feed items = AgentTenderFeedSerializer form, NO club field, and NO
//     you_contacted/chat_channel_id on the item (D-003). you_contacted comes from
//     getContacted()/getRequest(); channel_id only from messageAgent().
//   • N3 uses the backend spec names (position_primary, window, level_expectation,
//     additional_details, single position_secondary, flat terms_*), recipients
//     with agent.display_name + decline_reason.

const REQUESTS = [
  {
    id: 1,
    source: "agent",
    verification_status: "club_verified",
    tender_category: "player",
    position: "central_defender",
    other_staff_position: "",
    type_of_tender: "buy",
    recruiting_for: "professional",
    league_tier: 1,
    country: { name: "Germany", code: "DE" },
    budget_band: "€1–2M range",
    transfer_window: { label: "Summer 2026", opens_at: null, closes_at: null },
    foot: "right",
    minimum_height: 185,
    min_age: 20,
    max_age: 26,
    only_eu_player: false,
    only_free_players: false,
    commission_split: "",
    commission_split_note: "Negotiable",
    posted_agency: { name: "Weber Sports Group", logo_url: null },
    responsible_agent: { display_name: "Stefan Weber", avatar: null },
    trust_stats: {
      agent: { posted: 14, verified: 14, deals: 6 },
      agency: { posted: 40, verified: 31, deals: 18 },
    },
    created_on: "2026-07-04T09:00:00Z",
  },
  {
    id: 2,
    source: "agent",
    verification_status: "club_verified",
    tender_category: "player",
    position: "striker",
    other_staff_position: "",
    type_of_tender: "buy",
    recruiting_for: "professional",
    league_tier: 1,
    country: { name: "Austria", code: "AT" },
    budget_band: "€500k–1M range",
    transfer_window: { label: "Summer 2026", opens_at: null, closes_at: null },
    foot: "both",
    minimum_height: null,
    min_age: 22,
    max_age: 28,
    only_eu_player: false,
    only_free_players: false,
    commission_split: "",
    commission_split_note: "",
    posted_agency: { name: "Berg Management", logo_url: null },
    responsible_agent: { display_name: "Jonas Berg", avatar: null },
    trust_stats: { agent: { posted: 6, verified: 6, deals: 2 } },
    created_on: "2026-07-03T14:00:00Z",
  },
  {
    id: 3,
    source: "agent",
    verification_status: "club_verified",
    tender_category: "player",
    position: "left_winger",
    other_staff_position: "right_winger",
    type_of_tender: "buy",
    recruiting_for: "professional",
    league_tier: 1,
    country: { name: "Belgium", code: "BE" },
    budget_band: "€500k–1M range",
    transfer_window: { label: "Summer 2026", opens_at: null, closes_at: null },
    foot: "right",
    minimum_height: null,
    min_age: 19,
    max_age: 25,
    only_eu_player: true,
    only_free_players: false,
    commission_split: "",
    commission_split_note: "",
    posted_agency: { name: "Fournier Football", logo_url: null },
    responsible_agent: { display_name: "Élise Fournier", avatar: null },
    trust_stats: { agent: { posted: 21, verified: 21, deals: 11 } },
    created_on: "2026-07-02T10:00:00Z",
  },
  {
    id: 4,
    source: "agent",
    verification_status: "unverified",
    tender_category: "player",
    position: "central_midfielder",
    other_staff_position: "defensive_midfielder",
    type_of_tender: "loan",
    recruiting_for: "professional",
    league_tier: 2,
    country: { name: "Italy", code: "IT" },
    budget_band: "€100–300k range",
    transfer_window: { label: "Winter 2026/27", opens_at: null, closes_at: null },
    foot: "left",
    minimum_height: null,
    min_age: 21,
    max_age: 27,
    only_eu_player: false,
    only_free_players: false,
    commission_split: "",
    commission_split_note: "",
    posted_agency: { name: "Rinaldi & Partners", logo_url: null },
    responsible_agent: { display_name: "Marco Rinaldi", avatar: null },
    trust_stats: { agent: { posted: 9, verified: 9, deals: 4 } },
    created_on: "2026-07-01T16:00:00Z",
  },
];

// Which requests this club has already contacted (agent-granular). Request 2 →
// contacted, drives the "You contacted …" marker.
const CONTACTED_IDS = [2];

const SQUAD = [
  { id: "p1", name: "Luka Marić", position: "Centre-Back", age: 23 },
  { id: "p2", name: "Petar Novak", position: "Central Midfielder", age: 21 },
  { id: "p3", name: "Marin Babić", position: "Striker", age: 26 },
  { id: "p4", name: "Ivan Kovač", position: "Right Back", age: 24 },
];

const paramGet = (params, key) =>
  params instanceof URLSearchParams ? params.get(key) : params?.[key];

const delay = (data, ms = 220) =>
  new Promise((resolve) => setTimeout(() => resolve(data), ms));

const matchesFilters = (r, params) => {
  const pos = paramGet(params, "position");
  const country = paramGet(params, "country");
  const type = paramGet(params, "type_of_tender");
  const verified = paramGet(params, "verified") || paramGet(params, "verified_only");
  const search = (paramGet(params, "search") || "").toLowerCase();
  if (pos && r.position !== pos) return false;
  if (country && r.country?.name?.toLowerCase() !== country.toLowerCase()) return false;
  if (type && r.type_of_tender !== type) return false;
  if (verified === "true" && r.verification_status !== "club_verified") return false;
  if (search) {
    const hay = [
      r.position,
      r.posted_agency?.name,
      r.responsible_agent?.display_name,
      r.country?.name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(search)) return false;
  }
  return true;
};

const rank = (a, b) => {
  const av = a.verification_status === "club_verified" ? 0 : 1;
  const bv = b.verification_status === "club_verified" ? 0 : 1;
  if (av !== bv) return av - bv;
  return new Date(b.created_on) - new Date(a.created_on);
};

const clubNetworkMock = {
  getRequests: (params) => {
    const filtered = REQUESTS.filter((r) => matchesFilters(r, params)).sort(rank);
    return delay({
      success: true,
      data: filtered,
      pagination: {
        count: filtered.length,
        next: null,
        previous: null,
        currentPage: parseInt(paramGet(params, "page") || "1", 10),
      },
    });
  },

  getRequest: (id) => {
    const r = REQUESTS.find((x) => String(x.id) === String(id));
    return delay(
      r
        ? { success: true, data: { ...r, you_contacted: CONTACTED_IDS.includes(r.id) } }
        : { success: false, error: "Request not found." },
    );
  },

  getContacted: (ids) => {
    const set = (ids || []).map((x) => Number(x));
    const contacted = CONTACTED_IDS.filter((x) => set.includes(x));
    return delay({ success: true, data: contacted });
  },

  getSquad: () => delay({ success: true, data: SQUAD }),

  messageAgent: (id) => {
    if (!CONTACTED_IDS.includes(Number(id))) CONTACTED_IDS.push(Number(id));
    return delay({
      success: true,
      data: { channel_id: `direct_mock_${id}`, channel_type: "messaging", you_contacted: true },
    });
  },
};

// ── N3 — Trusted Agent mock state (real backend field names) ────────────────
// Agent identity blocks (record-hydrated shape: AgentIdentityField).
const AG = {
  davor: { id: "a1", display_name: "Davor Perić", avatar: null, agency_name: "Perić Sport Consult", is_licensed: true, license_verification_status: "fifa_verified" },
  stefan: { id: "a2", display_name: "Stefan Weber", avatar: null, agency_name: "Weber Sports Group", is_licensed: true, license_verification_status: "pending_review" },
  marco: { id: "a3", display_name: "Marco Rinaldi", avatar: null, agency_name: "Rinaldi & Partners", is_licensed: false, license_verification_status: "unverified" },
  elise: { id: "a4", display_name: "Élise Fournier", avatar: null, agency_name: "Fournier Football", is_licensed: true, license_verification_status: "fifa_verified" },
  jonas: { id: "a6", display_name: "Jonas Berg", avatar: null, agency_name: "Berg Management", is_licensed: false, license_verification_status: "failed_qr" },
};

const SEARCH_REQUESTS = [
  // ── ACTIVE bucket ──
  // Searching (A1 winner chain accepted → searching): Davor holds it, Stefan lost
  // the race, Marco declined. Resolution + row_resolution present.
  {
    id: 101,
    status: "active",
    created_at: "2026-05-12T09:00:00Z",
    updated_at: "2026-05-12T09:00:00Z",
    position_primary: "left_winger",
    position_secondary: "right_winger",
    age_min: 18,
    age_max: 24,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "Left-footed preferred. Pace over physique.",
    terms_enabled: true,
    salary_amount: 180000,
    salary_period: "per_year",
    commission_value: 10,
    commission_basis: "percentage_of_salary",
    commission_note: "",
    lifecycle_bucket: "active",
    winner: { id: "a1", name: "Davor Perić" },
    resolution_line: "Davor Perić accepted first, he has the job",
    delivered_count: null,
    contacts_opened: 0,
    recipients: [
      { id: "r1", agent: AG.davor, status: "searching", decline_reason: "", responded_at: "2026-05-13T10:00:00Z", accepted_at: "2026-05-13T10:00:00Z", seen_at: "2026-05-12T18:00:00Z", searching_since: "2026-05-14T09:00:00Z", delivered_count: null, row_resolution: null, created_at: "2026-05-12T09:00:00Z", chat_channel_id: "cn-chan-a1-101" },
      { id: "r2", agent: AG.stefan, status: "closed", decline_reason: "", responded_at: "2026-05-13T11:00:00Z", accepted_at: null, seen_at: "2026-05-12T20:00:00Z", searching_since: null, delivered_count: null, row_resolution: "Closed, Davor Perić was first", created_at: "2026-05-12T09:00:00Z" },
      { id: "r3", agent: AG.marco, status: "declined", decline_reason: "Outside my markets right now.", responded_at: "2026-05-14T08:00:00Z", accepted_at: null, seen_at: null, searching_since: null, delivered_count: null, row_resolution: null, created_at: "2026-05-12T09:00:00Z" },
    ],
  },
  // Waiting / pending (A14): nobody accepted — one Pending, one Seen. Withdraw.
  {
    id: 102,
    status: "active",
    created_at: "2026-07-06T09:00:00Z",
    updated_at: "2026-07-06T09:00:00Z",
    position_primary: "goalkeeper",
    position_secondary: "",
    age_min: 25,
    age_max: 32,
    transfer_type: "loan",
    window: "Winter 2026/27",
    level_expectation: "1",
    additional_details: "Experienced, immediate starter.",
    terms_enabled: false,
    salary_amount: null,
    salary_period: "per_year",
    commission_value: null,
    commission_basis: "percentage_of_salary",
    commission_note: "",
    lifecycle_bucket: "active",
    winner: null,
    resolution_line: null,
    delivered_count: null,
    contacts_opened: 0,
    recipients: [
      { id: "r4", agent: AG.elise, status: "seen", decline_reason: "", responded_at: null, accepted_at: null, seen_at: "2026-07-06T14:00:00Z", searching_since: null, delivered_count: null, row_resolution: null, created_at: "2026-07-06T09:00:00Z" },
      { id: "r5", agent: AG.stefan, status: "sent", decline_reason: "", responded_at: null, accepted_at: null, seen_at: null, searching_since: null, delivered_count: null, row_resolution: null, created_at: "2026-07-06T09:00:00Z" },
    ],
  },
  // ── ARCHIVE bucket ──
  // Delivered · 3 (A15): View proposals + "2 proposals, 1 contact opened".
  {
    id: 201,
    status: "active",
    created_at: "2026-05-12T09:00:00Z",
    updated_at: "2026-08-02T14:00:00Z",
    position_primary: "right_back",
    position_secondary: "",
    age_min: 20,
    age_max: 27,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "Two-way full-back, comfortable in a back three.",
    terms_enabled: true,
    salary_amount: 240000,
    salary_period: "per_year",
    commission_value: 8,
    commission_basis: "percentage_of_salary",
    commission_note: "",
    lifecycle_bucket: "archive",
    winner: { id: "a1", name: "Davor Perić" },
    resolution_line: "Davor Perić accepted first, he has the job",
    delivered_count: 3,
    contacts_opened: 1,
    recipients: [
      { id: "r6", agent: AG.davor, status: "delivered", decline_reason: "", responded_at: "2026-05-13T10:00:00Z", accepted_at: "2026-05-13T10:00:00Z", seen_at: "2026-05-12T18:00:00Z", searching_since: "2026-05-14T09:00:00Z", delivered_count: 3, row_resolution: null, created_at: "2026-05-12T09:00:00Z", chat_channel_id: "cn-chan-a1-201" },
      { id: "r7", agent: AG.elise, status: "closed", decline_reason: "", responded_at: "2026-05-13T12:00:00Z", accepted_at: null, seen_at: null, searching_since: null, delivered_count: null, row_resolution: "Closed, Davor Perić was first", created_at: "2026-05-12T09:00:00Z" },
    ],
  },
  // Nothing found (A15): Read reason.
  {
    id: 202,
    status: "active",
    created_at: "2026-07-06T09:00:00Z",
    updated_at: "2026-08-05T10:00:00Z",
    position_primary: "central_midfielder",
    position_secondary: "defensive_midfielder",
    age_min: 21,
    age_max: 28,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "2",
    additional_details: "Deep-lying playmaker, left-footed a plus.",
    terms_enabled: false,
    salary_amount: null,
    salary_period: "per_year",
    commission_value: null,
    commission_basis: "percentage_of_salary",
    commission_note: "",
    lifecycle_bucket: "archive",
    winner: { id: "a4", name: "Élise Fournier" },
    resolution_line: "Élise Fournier accepted first, he has the job",
    delivered_count: 0,
    contacts_opened: 0,
    recipients: [
      { id: "r8", agent: AG.elise, status: "nothing_found", decline_reason: "", responded_at: "2026-07-07T10:00:00Z", accepted_at: "2026-07-07T10:00:00Z", seen_at: "2026-07-06T13:00:00Z", searching_since: "2026-07-08T09:00:00Z", delivered_count: 0, row_resolution: null, created_at: "2026-07-06T09:00:00Z", chat_channel_id: "cn-chan-a4-202" },
    ],
  },
  // Closed by the club (A15 "Closed by you", Send again).
  {
    id: 203,
    status: "closed",
    created_at: "2026-06-01T09:00:00Z",
    updated_at: "2026-06-20T09:00:00Z",
    closed_at: "2026-06-20T09:00:00Z",
    position_primary: "striker",
    position_secondary: "",
    age_min: 22,
    age_max: 29,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    terms_enabled: false,
    salary_amount: null,
    salary_period: "per_year",
    commission_value: null,
    commission_basis: "percentage_of_salary",
    commission_note: "",
    lifecycle_bucket: "archive",
    winner: null,
    resolution_line: null,
    delivered_count: null,
    contacts_opened: 0,
    recipients: [
      { id: "r9", agent: AG.stefan, status: "closed", decline_reason: "", responded_at: "2026-06-20T09:00:00Z", accepted_at: null, seen_at: "2026-06-01T15:00:00Z", searching_since: null, delivered_count: null, row_resolution: null, created_at: "2026-06-01T09:00:00Z" },
    ],
  },
  // All declined (A15).
  {
    id: 204,
    status: "active",
    created_at: "2026-06-10T09:00:00Z",
    updated_at: "2026-06-15T09:00:00Z",
    position_primary: "left_back",
    position_secondary: "",
    age_min: 19,
    age_max: 24,
    transfer_type: "loan",
    window: "Winter 2026/27",
    level_expectation: "2",
    additional_details: "",
    terms_enabled: false,
    salary_amount: null,
    salary_period: "per_year",
    commission_value: null,
    commission_basis: "percentage_of_salary",
    commission_note: "",
    lifecycle_bucket: "archive",
    winner: null,
    resolution_line: null,
    delivered_count: null,
    contacts_opened: 0,
    recipients: [
      { id: "r10", agent: AG.marco, status: "declined", decline_reason: "Full book this window.", responded_at: "2026-06-12T09:00:00Z", accepted_at: null, seen_at: "2026-06-10T20:00:00Z", searching_since: null, delivered_count: null, row_resolution: null, created_at: "2026-06-10T09:00:00Z" },
      { id: "r11", agent: AG.jonas, status: "expired", decline_reason: "", responded_at: null, accepted_at: null, seen_at: null, searching_since: null, delivered_count: null, row_resolution: null, created_at: "2026-06-10T09:00:00Z" },
    ],
  },
];

// Anonymised feed-card content for A7 (AgentTenderFeedSerializer shape), keyed by
// search-request id — the LIVE commissioned request as the network sees it.
const COMMISSIONED_CARDS = {
  101: {
    id: 9101,
    source: "agent",
    verification_status: "club_verified",
    tender_category: "player",
    position: "left_winger",
    other_staff_position: "right_winger",
    type_of_tender: "buy",
    recruiting_for: "professional",
    league_tier: 1,
    country: { name: "Croatia", code: "HR" },
    budget_band: "€300–600k range",
    transfer_window: { label: "Summer 2026", opens_at: null, closes_at: null },
    foot: "left",
    minimum_height: null,
    min_age: 18,
    max_age: 24,
    only_eu_player: false,
    only_free_players: false,
    posted_agency: { name: "Perić Sport Consult", logo_url: null },
    responsible_agent: { display_name: "Davor Perić", avatar: null },
    trust_stats: { agent: { posted: 14, verified: 14, deals: 6 } },
    created_on: "2026-05-14T09:00:00Z",
  },
};

const AGENT_DIRECTORY = [
  { id: "a1", name: "Davor Perić", avatar: null, agency_name: "Perić Sport Consult", agency_country: "Croatia", is_licensed: true, license_verification_status: "fifa_verified" },
  { id: "a2", name: "Stefan Weber", avatar: null, agency_name: "Weber Sports Group", agency_country: "Germany", is_licensed: true, license_verification_status: "pending_review" },
  { id: "a4", name: "Élise Fournier", avatar: null, agency_name: "Fournier Football", agency_country: "France", is_licensed: true, license_verification_status: "fifa_verified" },
  { id: "a5", name: "Claude Fournier", avatar: null, agency_name: "Fournier Football", agency_country: "France", is_licensed: false, license_verification_status: "unverified" },
  { id: "a6", name: "Jonas Berg", avatar: null, agency_name: "Berg Management", agency_country: "Sweden", is_licensed: false, license_verification_status: "failed_qr" },
];

// Agent-side inbox rows (club FULLY visible, real inbox shape). The B7 seed
// covers EVERY agent bucket so the QCS-T7 visual gate can render each state:
// to_answer (B8 countdown + full terms + PDF), searching-accepted (B1 post-your-
// request card), searching-live (B4 counts + Deliver), delivered (B4 delivered),
// nothing_found, closed-loser (B5), club-closed-while-searching (B10), declined
// (with reason), expired (B11). agent_bucket mirrors the BE derivation.
const DAY = 86400;
const INBOX = [
  {
    id: 101,
    status: "active",
    created_at: "2026-08-06T09:00:00Z",
    updated_at: "2026-08-06T09:00:00Z",
    club: { id: "c1", name: "NK Adria Zagreb", country: "Croatia", logo: null },
    recipient_id: "r-inbox-1",
    recipient_status: "sent",
    agent_bucket: "to_answer",
    answer_deadline_days: 5,
    deadline_remaining_seconds: 3 * DAY + 4 * 3600,
    decline_reason: "",
    responded_at: null,
    has_document: true,
    position_primary: "left_winger",
    position_secondary: "right_winger",
    age_min: 18,
    age_max: 24,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "Left-footed preferred, pace over physique.",
    terms_enabled: true,
    salary_amount: 180000,
    salary_period: "per_year",
    salary_basis: "gross",
    commission_value: 10,
    commission_basis: "percentage_of_salary",
    commission_payout_mode: "one_total",
    commission_note: "We move fast on the right profile.",
    maximum_transfer_fee: 2500000,
    only_free_players: false,
  },
  {
    id: 102,
    status: "active",
    created_at: "2026-08-07T09:00:00Z",
    updated_at: "2026-08-07T09:00:00Z",
    club: { id: "c9", name: "Real Montaña CF", country: "Spain", logo: null },
    recipient_id: "r-inbox-9",
    recipient_status: "seen",
    agent_bucket: "to_answer",
    answer_deadline_days: 7,
    deadline_remaining_seconds: 6 * 3600,
    decline_reason: "",
    responded_at: null,
    has_document: false,
    tender_category: "staff",
    staff_role: "sporting_director",
    staff_role_display: "Sporting Director",
    recruiting_for_display: "Professional",
    position_primary: null,
    position_secondary: "",
    age_min: null,
    age_max: null,
    transfer_type: "full_time",
    window: "",
    level_expectation: "2",
    additional_details: "Track record building a recruitment department.",
    terms_enabled: true,
    salary_amount: 90000,
    salary_period: "per_year",
    salary_basis: "net",
    commission_value: 8000,
    commission_basis: "fixed_fee",
    commission_payout_mode: "each_separate",
    commission_note: "",
    only_free_players: true,
  },
  {
    id: 103,
    status: "active",
    created_at: "2026-07-30T09:00:00Z",
    updated_at: "2026-07-30T09:00:00Z",
    club: { id: "c2", name: "SC Bergwald", country: "Austria", logo: null },
    recipient_id: "r-inbox-2",
    recipient_status: "accepted",
    agent_bucket: "searching",
    decline_reason: "",
    responded_at: "2026-08-04T09:00:00Z",
    has_document: true,
    accepted_players_count: null,
    replies_count: null,
    delivered_count: null,
    position_primary: "right_back",
    position_secondary: "",
    age_min: 20,
    age_max: 27,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    terms_enabled: true,
    salary_amount: 120000,
    salary_period: "per_year",
    salary_basis: "gross",
    commission_value: 12,
    commission_basis: "percentage_of_salary",
    commission_payout_mode: "one_total",
    commission_note: "",
    only_free_players: false,
  },
  {
    id: 104,
    status: "active",
    created_at: "2026-07-10T09:00:00Z",
    updated_at: "2026-07-10T09:00:00Z",
    club: { id: "c3", name: "FC Lumière", country: "France", logo: null },
    recipient_id: "r-inbox-3",
    recipient_status: "searching",
    agent_bucket: "searching",
    decline_reason: "",
    responded_at: "2026-07-12T09:00:00Z",
    has_document: true,
    accepted_players_count: 3,
    replies_count: 7,
    delivered_count: null,
    position_primary: "striker",
    position_secondary: "",
    age_min: 21,
    age_max: 29,
    transfer_type: "loan",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "Proven goalscorer, loan with option preferred.",
    terms_enabled: true,
    salary_amount: 15000,
    salary_period: "per_month",
    salary_basis: "net",
    commission_value: 10,
    commission_basis: "percentage_of_salary",
    commission_payout_mode: "one_total",
    commission_note: "",
    loan_fee_enabled: true,
    loan_fee_amount: 400000,
    loan_option_to_buy: true,
    only_free_players: false,
  },
  {
    id: 105,
    status: "closed",
    created_at: "2026-06-01T09:00:00Z",
    updated_at: "2026-07-20T09:00:00Z",
    club: { id: "c4", name: "Athletic Nord", country: "Germany", logo: null },
    recipient_id: "r-inbox-4",
    recipient_status: "delivered",
    agent_bucket: "delivered",
    decline_reason: "",
    responded_at: "2026-07-18T09:00:00Z",
    chat_channel_id: "direct_mock_105",
    accepted_players_count: 5,
    replies_count: 9,
    delivered_count: 4,
    position_primary: "central_midfielder",
    position_secondary: "",
    age_min: 22,
    age_max: 28,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    terms_enabled: false,
  },
  {
    id: 106,
    status: "closed",
    created_at: "2026-06-05T09:00:00Z",
    updated_at: "2026-07-01T09:00:00Z",
    club: { id: "c5", name: "Costa Verde FC", country: "Portugal", logo: null },
    recipient_id: "r-inbox-5",
    recipient_status: "nothing_found",
    agent_bucket: "delivered",
    decline_reason: "",
    responded_at: "2026-06-28T09:00:00Z",
    chat_channel_id: "direct_mock_106",
    accepted_players_count: 0,
    replies_count: 2,
    delivered_count: 0,
    position_primary: "goalkeeper",
    position_secondary: "",
    age_min: 24,
    age_max: 32,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "2",
    additional_details: "",
    terms_enabled: false,
  },
  {
    id: 107,
    status: "active",
    created_at: "2026-07-22T09:00:00Z",
    updated_at: "2026-07-25T09:00:00Z",
    club: { id: "c6", name: "Vale United", country: "England", logo: null },
    recipient_id: "r-inbox-6",
    recipient_status: "closed",
    agent_bucket: "archive",
    decline_reason: "",
    responded_at: "2026-07-24T09:00:00Z",
    position_primary: "left_back",
    position_secondary: "",
    age_min: 19,
    age_max: 25,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    terms_enabled: false,
  },
  {
    id: 108,
    status: "closed",
    created_at: "2026-06-15T09:00:00Z",
    updated_at: "2026-07-15T09:00:00Z",
    club: { id: "c7", name: "Nordborg IF", country: "Sweden", logo: null },
    recipient_id: "r-inbox-7",
    recipient_status: "closed",
    agent_bucket: "archive",
    decline_reason: "",
    responded_at: "2026-07-14T09:00:00Z",
    chat_channel_id: "direct_mock_108",
    accepted_players_count: 2,
    replies_count: 5,
    delivered_count: null,
    position_primary: "right_winger",
    position_secondary: "",
    age_min: 20,
    age_max: 26,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    terms_enabled: false,
  },
  {
    id: 109,
    status: "active",
    created_at: "2026-07-05T09:00:00Z",
    updated_at: "2026-07-08T09:00:00Z",
    club: { id: "c8", name: "Delta Rovers", country: "Ireland", logo: null },
    recipient_id: "r-inbox-8",
    recipient_status: "declined",
    agent_bucket: "archive",
    decline_reason: "Outside my markets right now.",
    responded_at: "2026-07-08T09:00:00Z",
    position_primary: "attacking_midfielder",
    position_secondary: "",
    age_min: 18,
    age_max: 23,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "2",
    additional_details: "",
    terms_enabled: false,
  },
  {
    id: 110,
    status: "active",
    created_at: "2026-07-01T09:00:00Z",
    updated_at: "2026-07-06T09:00:00Z",
    club: { id: "c10", name: "AC Rialto", country: "Italy", logo: null },
    recipient_id: "r-inbox-10",
    recipient_status: "expired",
    agent_bucket: "archive",
    answer_deadline_days: 5,
    deadline_remaining_seconds: 0,
    decline_reason: "",
    responded_at: null,
    position_primary: "centre_back",
    position_secondary: "",
    age_min: 23,
    age_max: 30,
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    terms_enabled: false,
  },
];

// ── QCS-T5 Delivery seed (Annex A2/A6·b/B3) ─────────────────────────────────
// Accepted players on the commissioned request — the deliver checklist's rows
// (B3). Player identity is the hydrated record shape (PlayerMinimalSerializer:
// image/name/position/age/market_value(+currency)/contract_until) plus the
// player's OWN agent — exactly what the proposal cards render, so nothing is
// retyped. Keyed by search-request id.
const ACCEPTED_PLAYERS = {
  103: [
    {
      id: 5001,
      name: "Luka Moretti",
      image: null,
      position: "Right Back",
      age: 23,
      market_value: 4500000,
      market_value_currency: "EUR",
      contract_until: "2027-06-30",
      club_name: "US Trapani",
      nationality: { name: "Italy", image_url: null },
      player_agent: { id: 9001, name: "Giulia Conti", agency: "Conti Football" },
    },
    {
      id: 5002,
      name: "Emil Andersson",
      image: null,
      position: "Right Back",
      age: 21,
      market_value: 2200000,
      market_value_currency: "EUR",
      contract_until: "2026-12-31",
      club_name: "Malmö FF",
      nationality: { name: "Sweden", image_url: null },
      player_agent: { id: 9002, name: "Henrik Ohlsson", agency: "Nordic Sports" },
    },
    {
      id: 5003,
      name: "Diego Fuentes",
      image: null,
      position: "Right Wing-Back",
      age: 25,
      market_value: 6800000,
      market_value_currency: "EUR",
      contract_until: "2028-06-30",
      club_name: "CA Osasuna",
      nationality: { name: "Spain", image_url: null },
      player_agent: { id: 9003, name: "Rafael Núñez", agency: "Iberia Talents" },
    },
  ],
};

// The delivered package a club sees on A2, keyed by search-request id. Shape =
// ClubDeliverySerializer (agent = AgentIdentityField, proposals = A6·b cards).
const DELIVERIES = {
  201: {
    id: 700,
    is_empty: false,
    empty_reason: "",
    summary_note:
      "Three right-backs that fit the brief. Fuentes is the standout on pace and " +
      "final-third output; Moretti is the value play with a 2027 deal.",
    delivered_at: "2026-08-02T14:00:00Z",
    searching_since: "2026-05-12T09:00:00Z",
    proposal_count: 3,
    agent: {
      id: "a1",
      display_name: "Davor Perić",
      avatar: null,
      agency_name: "Perić Sport Consult",
      is_licensed: true,
      license_verification_status: "fifa_verified",
    },
    proposals: [
      {
        id: 8001,
        note: "First choice. Right-footed, overlaps well, contract runs to 2028 so no urgency on price.",
        player: {
          id: 5003,
          name: "Diego Fuentes",
          image: null,
          position: "Right Wing-Back",
          age: 25,
          market_value: 6800000,
          market_value_currency: "EUR",
          contract_until: "2028-06-30",
          club_name: "CA Osasuna",
          nationality: { name: "Spain", image_url: null },
        },
        player_agent: { id: 9003, name: "Rafael Núñez", agency: "Iberia Talents" },
      },
      {
        id: 8002,
        note: "Best value. 2027 deal, open to a move, agent already sounded out.",
        player: {
          id: 5001,
          name: "Luka Moretti",
          image: null,
          position: "Right Back",
          age: 23,
          market_value: 4500000,
          market_value_currency: "EUR",
          contract_until: "2027-06-30",
          club_name: "US Trapani",
          nationality: { name: "Italy", image_url: null },
        },
        player_agent: { id: 9001, name: "Giulia Conti", agency: "Conti Football" },
      },
      {
        id: 8003,
        note: "",
        player: {
          id: 5002,
          name: "Emil Andersson",
          image: null,
          position: "Right Back",
          age: 21,
          market_value: 2200000,
          market_value_currency: "EUR",
          contract_until: "2026-12-31",
          club_name: "Malmö FF",
          nationality: { name: "Sweden", image_url: null },
        },
        player_agent: { id: 9002, name: "Henrik Ohlsson", agency: "Nordic Sports" },
      },
    ],
  },
  // Empty delivery (A3) — an honest end, reason instead of cards.
  202: {
    id: 701,
    is_empty: true,
    empty_reason:
      "I worked three markets for eight weeks. The two profiles that fit your budget " +
      "renewed before we could move, and nothing else cleared the bar you set. I would " +
      "rather tell you straight than send names that don't fit.",
    summary_note: "",
    delivered_at: "2026-08-05T10:00:00Z",
    searching_since: "2026-07-06T09:00:00Z",
    proposal_count: 0,
    agent: {
      id: "a4",
      display_name: "Élise Fournier",
      avatar: null,
      agency_name: "Fournier Football",
      is_licensed: true,
      license_verification_status: "fifa_verified",
    },
    proposals: [],
  },
};

// Keep the delivered date recent so the commission-chat countdown chip reads a
// live "expires in N days" (Annex A2) rather than a stale past date.
DELIVERIES[201].delivered_at = new Date(Date.now() - 2 * 86400000).toISOString();

let nextRequestId = 200;

const bucketOf = (r) => r.lifecycle_bucket || (r.status === "closed" ? "archive" : "active");

Object.assign(clubNetworkMock, {
  // A15: filter by lifecycle bucket, always return the active/archive counts.
  listSearchRequests: ({ status, bucket } = {}) => {
    const activeCount = SEARCH_REQUESTS.filter((r) => bucketOf(r) === "active").length;
    const archiveCount = SEARCH_REQUESTS.length - activeCount;
    let data = SEARCH_REQUESTS;
    if (status) data = data.filter((r) => r.status === status);
    if (bucket) data = data.filter((r) => bucketOf(r) === bucket);
    return delay({
      success: true,
      data,
      activeCount,
      archiveCount,
      pagination: { count: data.length },
    });
  },

  withdrawSearchRequest: (id) => {
    const r = SEARCH_REQUESTS.find((x) => String(x.id) === String(id));
    if (r) {
      r.status = "closed";
      r.lifecycle_bucket = "archive";
      (r.recipients || []).forEach((rec) => {
        if (rec.status === "sent" || rec.status === "seen") rec.status = "closed";
      });
    }
    return delay({ success: true, data: { id, status: "closed" } });
  },

  markSeen: (id) =>
    delay({ success: true, data: { id, status: "seen", seen_at: new Date().toISOString() } }),

  getCommissionedRequest: (id) => {
    const card = COMMISSIONED_CARDS[id];
    if (!card) {
      return delay({ success: false, status: 404, error: "No live commissioned request yet." });
    }
    return delay({
      success: true,
      data: { commissioned_by_you: true, club_verified: true, request: card },
    });
  },

  getSaved: () => delay({ success: true, data: [] }),
  saveRequest: () => delay({ success: true }),
  unsaveRequest: () => delay({ success: true }),

  getSearchRequest: (id) => {
    const r = SEARCH_REQUESTS.find((x) => String(x.id) === String(id));
    if (!r) return delay({ success: false, status: 404, error: "Request not found." });
    return delay({ success: true, data: r });
  },

  getSearchRequestDocument: (id) => {
    const r = SEARCH_REQUESTS.find((x) => String(x.id) === String(id));
    if (!r || !r.has_document) {
      return delay({ success: false, status: 404, error: "No document attached." });
    }
    return delay({ success: true, data: { download_url: "https://example.com/mock-document.pdf" } });
  },

  createSearchRequest: (payload) => {
    const recipients = (payload.recipient_agent_ids || []).map((aid, i) => {
      const agent = AGENT_DIRECTORY.find((a) => a.id === aid) || { id: aid, name: "Agent", is_licensed: false };
      return {
        id: `nr${nextRequestId}_${i}`,
        agent: {
          id: agent.id,
          display_name: agent.name,
          avatar: agent.avatar || null,
          agency_name: agent.agency_name || "",
          is_licensed: !!agent.is_licensed,
          license_verification_status:
            agent.license_verification_status || "unverified",
        },
        status: "sent",
        decline_reason: "",
        responded_at: null,
        created_at: new Date().toISOString(),
      };
    });
    const created = {
      id: nextRequestId++,
      status: "active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      position_primary: payload.position_primary,
      position_secondary: payload.position_secondary || "",
      age_min: payload.age_min,
      age_max: payload.age_max,
      transfer_type: payload.transfer_type,
      window: payload.window,
      level_expectation: payload.level_expectation,
      additional_details: payload.additional_details || "",
      terms_enabled: !!payload.terms_enabled,
      salary_amount: payload.salary_amount ?? null,
      salary_period: payload.salary_period || "per_year",
      commission_value: payload.commission_value ?? null,
      commission_basis: payload.commission_basis || "percentage_of_salary",
      commission_note: payload.commission_note || "",
      lifecycle_bucket: "active",
      winner: null,
      resolution_line: null,
      delivered_count: null,
      contacts_opened: 0,
      recipients,
    };
    SEARCH_REQUESTS.unshift(created);
    return delay({ success: true, data: created });
  },

  closeSearchRequest: (id) => {
    const r = SEARCH_REQUESTS.find((x) => String(x.id) === String(id));
    if (r) {
      r.status = "closed";
      r.lifecycle_bucket = "archive";
    }
    return delay({ success: true, data: { id, status: "closed" } });
  },

  searchAgents: (q = "") => {
    const query = String(q).toLowerCase();
    const data = query
      ? AGENT_DIRECTORY.filter(
          (a) =>
            a.name.toLowerCase().includes(query) ||
            a.agency_name.toLowerCase().includes(query),
        )
      : AGENT_DIRECTORY;
    return delay({ success: true, data });
  },

  listInbox: (status) => {
    const data = status ? INBOX.filter((x) => x.recipient_status === status) : INBOX;
    return delay({ success: true, data, pagination: { count: data.length } });
  },

  getCommissionPrefill: (id) =>
    delay({
      success: true,
      data: {
        search_request_id: id,
        commissioning_club_id: "c2",
        verified_from_birth: true,
        locked_fields: [
          "tender_category",
          "only_free_players",
          "recruiting_for",
          "position",
          "type_of_tender",
          "min_age",
          "max_age",
          "market_value_type",
          "currency",
        ],
        prefill: {
          tender_category: "player",
          only_free_players: false,
          recruiting_for: "professional",
          position: "right_back",
          type_of_tender: "buy",
          min_age: 20,
          max_age: 27,
          market_value_type: "not_important",
          currency: "EUR",
          description: "Right-back with attacking output, summer window.",
        },
        terms_not_carried: [
          "terms_enabled",
          "salary_amount",
          "commission_value",
          "maximum_transfer_fee",
        ],
        no_tender_pendant: ["position_secondary", "window", "level_expectation"],
      },
    }),

  acceptInbox: (id) => {
    const item = INBOX.find((x) => String(x.id) === String(id));
    if (item) {
      item.status = "active";
      item.recipient_status = "accepted";
      item.agent_bucket = "searching";
      item.responded_at = new Date().toISOString();
    }
    return delay({
      success: true,
      data: {
        id,
        recipient_id: item?.recipient_id,
        status: "accepted",
        channel: { channel_id: `direct_mock_${id}`, channel_type: "messaging" },
      },
    });
  },

  declineInbox: (id, reason) => {
    const item = INBOX.find((x) => String(x.id) === String(id));
    if (item) {
      item.recipient_status = "declined";
      item.agent_bucket = "archive";
      item.decline_reason = reason || "";
      item.responded_at = new Date().toISOString();
    }
    return delay({
      success: true,
      data: { id, recipient_id: item?.recipient_id, status: "declined", decline_reason: reason || "" },
    });
  },

  // ── QCS-T5 Delivery mocks ─────────────────────────────────────────────────
  getAcceptedPlayers: (id) =>
    delay({ success: true, data: ACCEPTED_PLAYERS[id] || ACCEPTED_PLAYERS[103] }),

  deliver: (id, { proposals = [], summaryNote = "", emptyReason = "" } = {}) => {
    const isEmpty = proposals.length === 0;
    if (isEmpty && !emptyReason.trim()) {
      return delay({
        success: false,
        status: 400,
        error: "A reason is required to deliver with no players.",
      });
    }
    return delay({
      success: true,
      data: {
        id: 999,
        is_empty: isEmpty,
        empty_reason: isEmpty ? emptyReason : "",
        summary_note: summaryNote,
        proposal_count: proposals.length,
        recipient_status: isEmpty ? "nothing_found" : "delivered",
      },
    });
  },

  getDelivery: (id) => {
    const d = DELIVERIES[id] || DELIVERIES[201];
    if (!d) return delay({ success: false, status: 404, error: "No delivery yet." });
    return delay({ success: true, data: d });
  },

  contactAgent: (id, playerId) =>
    delay({
      success: true,
      data: {
        search_request_id: id,
        player_id: playerId,
        channel: { channel_id: `contact_mock_${id}_${playerId}`, channel_type: "messaging" },
        context_card: { player_id: playerId },
      },
    }),
});

export default clubNetworkMock;
